import { useEffect, useRef } from 'react';

interface UsePollingOptions {
    /** Time between polls, in milliseconds. */
    intervalMs: number;
    /** When false the hook is fully idle: no timer and no listeners. Defaults to true. */
    enabled?: boolean;
}

/**
 * Calls `callback` every `intervalMs` while `enabled` and the browser tab is visible.
 *
 * - Pauses while the tab is hidden (no background traffic) and polls once right away when the
 *   tab becomes visible again, so a returning user never sees data that is a full interval stale.
 * - Never overlaps: if the previous call is still running, the next tick is skipped.
 * - Always calls the latest `callback`, so callers can pass an inline function that reads current
 *   props/state without restarting the timer on every render.
 * - Errors are the callback's responsibility. A rejection is swallowed here so one failed poll
 *   cannot surface as an unhandled rejection; the next tick simply tries again.
 */
export const usePolling = (callback: () => void | Promise<void>, { intervalMs, enabled = true }: UsePollingOptions): void => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        if (!enabled) return undefined;

        let isInFlight = false;
        let intervalId: number | undefined;

        const poll = async () => {
            if (isInFlight) return;
            isInFlight = true;
            try {
                await callbackRef.current();
            } catch (_error) {
                // Intentionally ignored; see the doc comment above.
            } finally {
                isInFlight = false;
            }
        };

        const start = () => {
            if (intervalId === undefined) intervalId = window.setInterval(poll, intervalMs);
        };
        const stop = () => {
            if (intervalId !== undefined) {
                window.clearInterval(intervalId);
                intervalId = undefined;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                poll();
                start();
            } else {
                stop();
            }
        };

        if (document.visibilityState === 'visible') start();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, intervalMs]);
};

export default usePolling;
